import { type CommentThreadJSON } from '@databases/model/commentThreads';
import { type ObjectMode } from '@databases/types';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { createField, getExistingDatabase } from '@databases/utils/database/utils';

import { databasesToContentId } from './constants.ts';

const generateComments = (
  baseDatabase: Database<ObjectMode.EXISTING>,
  fieldIds: string[]
) => {
  const commentThreads: Record<string, [CommentThreadJSON]> = {};

  const entries = baseDatabase.entries;
  commentThreads[`${entries[0]}-${fieldIds[3]}`] = [
    {
      threadId: 'thread-1',
      isResolved: false,
      commentCount: 2,
      versionNumber: 0
    }
  ];

  return commentThreads;
};

const getValueForType = (
  fieldType: DatabaseFieldType,
  entryIndex: number,
  fieldIndex: number
) => {
  switch (fieldType) {
    case DatabaseFieldType.TEXT:
      return { plain: `${entryIndex}-${fieldIndex}` };
    case DatabaseFieldType.ATTACHMENT:
      return [
        // use the existing template cover images for attachments
        {
          fileId: 'vendor-management-cover-1',
          id: ''
        }
      ];
  }
};

const generateEntriesById = (
  baseDatabase: Database<ObjectMode.CREATE>,
  fields: DatabaseField[]
) => {
  const result: Record<string, DatabaseEntry> = {};

  Object.values(baseDatabase.entriesById).forEach((entry, entryIndex) => {
    result[entry.id] = {
      id: entry.id,
      values: Object.fromEntries(
        fields.map((field, fieldIndex) => [
          field.id,
          {
            type: field.type,
            value: getValueForType(field.type, entryIndex, fieldIndex)
          }
        ])
      ) as Record<string, DatabaseValue<DatabaseFieldType, object>>
    };
  });

  return result;
};

export const getDatabaseWithAttachments = (): Database<ObjectMode.EXISTING> => {
  const baseDatabase = getExistingDatabase({
    contentId: databasesToContentId['databaseWithAttachments']
  });

  const fields = [
    {
      ...createField(DatabaseFieldType.TEXT),
      name: 'Text field 1'
    },
    {
      ...createField(DatabaseFieldType.TEXT),
      name: 'Text field 2'
    },
    {
      ...createField(DatabaseFieldType.TEXT),
      name: 'Text field 3'
    },
    {
      ...createField(DatabaseFieldType.ATTACHMENT),
      name: 'Attachment field'
    }
  ];

  const entriesById = generateEntriesById(baseDatabase, fields);
  const fieldIds = fields.map((field) => field.id);

  const database = {
    ...baseDatabase,
    fields: fieldIds,
    fieldsById: Object.fromEntries(fields.map((field) => [field.id, field])),
    entriesById,
    entries: Object.keys(entriesById)
  };

  return {
    ...database,
    commentThreads: generateComments(baseDatabase, fieldIds)
  };
};
